/**
 * TeleprompterMain.jsx  ·  Refactored with Context API (fully updated)
 *
 * Changes vs original:
 *
 *  handleCmd
 *    • JUMP_TO { index: N } now handled → calls jumpTo(N)
 *
 *  handleRTCConnected
 *    • Accepts second arg replaceVideoTrack and stores in replaceVideoTrackRef
 *    • Clears ref on disconnect
 *
 *  bgMode effect
 *    • Calls replaceVideoTrack(track) whenever bgMode changes so the remote
 *      mirror sees the host's camera or screen share in real time
 *    • Ensures track.enabled = true before sending (Android fix)
 *
 *  pushState (WebRTC branch)
 *    • Sends full words array instead of words_count so Mirror tab populates
 *
 *  PrompterStage
 *    • Drag-to-scroll via Pointer Events (mouse + touch)
 *    • Tap-vs-drag guard on word clicks (4 px threshold)
 *    • cursor: grab, touchAction: none on the scroll stage
 */

import React, {
  useState, useEffect, useRef, useCallback, useMemo,
  createContext, useContext,
} from "react";

import {
  DEFAULT_SCRIPT,
  FONTS, FONT_LABELS,
  SPEED_PRESETS,
  QUICK_CUES,
  IS_PRO,
} from "./constants";

import {
  loadPrefs, savePrefs,
  parseScript, tokensToWords, tokensToRaw,
  buildSections, notesAtWord,
  splitIntoSections,
  parseWords, formatTime, estimateReadTime,
  genRoomId,
} from "./scriptUtils";

import { useWakeLock, useToast } from "./hooks";

import {
  QRIcon,
  CamIcon, ScrIcon, RecIcon, StopRecIcon, DlIcon,
  CloseIcon,
  ShareIcon, CopyClipboardIcon,
  FullscreenEnterIcon, FullscreenExitIcon,
  EditIcon, MirrorIcon, SectionsIcon, NotesIcon, HelpIcon, GridIcon,
  StopIcon, PlayIcon, PauseIcon, CueLabelIcon,
} from "./Overlays";

import {
  ScriptDiffModal,
  ClipsModal,
  EditModal,
  HelpModal,
} from "./Modals";

import { WebRTCModal } from "./RemoteComponents";


// ─────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────
const CUE_DURATION = 6000;


// ─────────────────────────────────────────────────────────────
//  Context
// ─────────────────────────────────────────────────────────────
const TeleprompterContext = createContext(null);

export function useTeleprompter() {
  const ctx = useContext(TeleprompterContext);
  if (!ctx) throw new Error("useTeleprompter must be used within <TeleprompterProvider>");
  return ctx;
}


// ─────────────────────────────────────────────────────────────
//  Provider
// ─────────────────────────────────────────────────────────────
function TeleprompterProvider({ children, onLogout }) {
  const isPro = IS_PRO;

  const proRedirect = useCallback(() => { window.location.href = "https://www.google.com"; }, []);
  const proAction   = useCallback((fn) => (isPro ? fn : proRedirect), [isPro, proRedirect]);

  const initialOffer = useMemo(() => {
    if (!isPro) return null;
    try {
      const raw = new URLSearchParams(window.location.search).get("offer");
      return raw ? decodeURIComponent(raw) : null;
    } catch { return null; }
  }, [isPro]);

  const prefs = useMemo(() => loadPrefs(), []);

  // ── Script / token state ────────────────────────────────────
  const [tokens,   setTokens]   = useState(() => {
    const saved = loadPrefs().script;
    return parseScript(saved?.trim() ? saved : DEFAULT_SCRIPT);
  });
  const [words,    setWords]    = useState(() => tokensToWords(tokens));
  const sections = useMemo(() => buildSections(tokens), [tokens]);

  // ── Playback state ──────────────────────────────────────────
  const [current,   setCurrent]   = useState(-1);
  const [playing,   setPlaying]   = useState(false);
  const [speed,     setSpeed]     = useState(prefs.speed    ?? 4);
  const [status,    setStatus]    = useState("READY");
  const [countdown, setCountdown] = useState(null);

  // ── Display state ───────────────────────────────────────────
  const [fontSize,     setFontSize]     = useState(prefs.fontSize   ?? 36);
  const [mirrored,     setMirrored]     = useState(prefs.mirrored   ?? false);
  const [font,         setFont]         = useState(prefs.font       ?? "serif");
  const [bgMode,       setBgMode]       = useState("off");
  const [bgOpacity,    setBgOpacity]    = useState(0.65);
  const [brightness,   setBrightness]   = useState(prefs.brightness ?? 100);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Modal / panel state ─────────────────────────────────────
  const [editOpen,     setEditOpen]     = useState(false);
  const [helpOpen,     setHelpOpen]     = useState(false);
  const [showQR,       setShowQR]       = useState(isPro && !!initialOffer);
  const [showClips,    setShowClips]    = useState(false);
  const [showCuePanel, setShowCuePanel] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [showNotes,    setShowNotes]    = useState(true);

  // ── Script edit draft ───────────────────────────────────────
  const [draftScript, setDraftScript] = useState(
    prefs.script?.trim() ? prefs.script : DEFAULT_SCRIPT
  );
  const [draftCursor, setDraftCursor] = useState(null);

  // ── Script diff modal ───────────────────────────────────────
  const [diffOpen,      setDiffOpen]      = useState(false);
  const [pendingScript, setPendingScript] = useState("");

  // ── Presenter notes ─────────────────────────────────────────
  const [activeNotes, setActiveNotes] = useState([]);

  // ── Cue banner ──────────────────────────────────────────────
  const [activeCue, setActiveCue] = useState(null);

  // ── Recording ───────────────────────────────────────────────
  const [recording,  setRecording]  = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [clips,      setClips]      = useState([]);

  // ── Remote connections ──────────────────────────────────────
  const [bcRemoteConnected,  setBcRemoteConnected]  = useState(false);
  const [rtcRemoteConnected, setRtcRemoteConnected] = useState(false);
  const [rtcDC,              setRtcDC]              = useState(null);
  const remoteConnected = isPro && (bcRemoteConnected || rtcRemoteConnected);

  // ── Toast / wake lock ───────────────────────────────────────
  const { toastMsg, toastVisible, showToast } = useToast();
  const {
    active: wakeActive, supported: wakeSupported,
    toggle: wakeToggle, acquire: wakeAcquire, release: wakeRelease,
  } = useWakeLock();

  // ── Room ID ─────────────────────────────────────────────────
  const roomId = useMemo(() => {
    if (!isPro) return null;
    let id = sessionStorage.getItem("tp-room");
    if (!id) { id = genRoomId(); sessionStorage.setItem("tp-room", id); }
    return id;
  }, [isPro]);

  // ── Custom Countdown state ──────────────────────────────────
  const [customCdOpen,      setCustomCdOpen]      = useState(false);
  const [customCdInput,     setCustomCdInput]      = useState("30");
  const [customCdActive,    setCustomCdActive]    = useState(false);
  const [customCdRemaining, setCustomCdRemaining] = useState(0);
  const customCdRef = useRef(null);

  // ── Refs ────────────────────────────────────────────────────
  const rootRef              = useRef(null);
  const stageRef             = useRef(null);
  const wordRefs             = useRef({});
  const textareaRef          = useRef(null);
  const timerRef             = useRef(null);
  const cdownRef             = useRef(null);
  const videoRef             = useRef(null);
  const streamRef            = useRef(null);
  const camRecStreamRef      = useRef(null);
  const mediaRecRef          = useRef(null);
  const recChunksRef         = useRef([]);
  const recTimerRef          = useRef(null);
  const recSecondsRef        = useRef(0);
  const bcChannelRef         = useRef(null);
  const pushStateTimer       = useRef(null);
  const handleCmdRef         = useRef(null);
  const replaceVideoTrackRef = useRef(null); // stores fn from WebRTCModal host path

  // Stable ref mirrors
  const playingRef = useRef(playing);
  const speedRef   = useRef(speed);
  const wordsRef   = useRef(words);
  const currentRef = useRef(current);
  const statusRef  = useRef(status);
  const rtcDCRef   = useRef(null);

  playingRef.current = playing;
  speedRef.current   = speed;
  wordsRef.current   = words;
  currentRef.current = current;
  statusRef.current  = status;
  rtcDCRef.current   = rtcDC;

  useEffect(() => () => { wordRefs.current = {}; }, []);

  // ── Persist prefs ───────────────────────────────────────────
  useEffect(() => { savePrefs({ speed });      }, [speed]);
  useEffect(() => { savePrefs({ fontSize });   }, [fontSize]);
  useEffect(() => { savePrefs({ font });       }, [font]);
  useEffect(() => { savePrefs({ mirrored });   }, [mirrored]);
  useEffect(() => { savePrefs({ brightness }); }, [brightness]);

  // ── State push (remote sync) ────────────────────────────────
  const pushState = useCallback((ch) => {
    if (!isPro) return;

    // BroadcastChannel — full payload including words array
    const payload = {
      type: "STATE",
      payload: {
        current: currentRef.current,
        words: wordsRef.current,
        playing: playingRef.current,
        status: statusRef.current,
        speed: speedRef.current,
      },
    };
    (ch || bcChannelRef.current)?.postMessage(payload);

    // WebRTC — send full words array so Mirror tab can populate
    if (rtcDCRef.current?.readyState === "open") {
      rtcDCRef.current.send(JSON.stringify({
        type: "STATE",
        current: currentRef.current,
        words: wordsRef.current,          // full array, not words_count
        playing: playingRef.current,
        status: statusRef.current,
        speed: speedRef.current,
      }));
    }
  }, [isPro]);

  const schedulePushState = useCallback(() => {
    if (!isPro) return;
    clearTimeout(pushStateTimer.current);
    pushStateTimer.current = setTimeout(() => pushState(), 80);
  }, [isPro, pushState]);

  useEffect(() => { schedulePushState(); }, [current, playing, status, speed, words, schedulePushState]);

  // ── Send video track to remote whenever bgMode changes ──────
  // This is what makes cam/screen actually appear on the remote mirror.
  useEffect(() => {
    const replaceFn = replaceVideoTrackRef.current;
    if (!replaceFn) return;
    const track = streamRef.current?.getVideoTracks()[0] ?? null;
    if (track) track.enabled = true; // ensure Android senders are live
    replaceFn(track);
  }, [bgMode]);

  // ── BroadcastChannel ────────────────────────────────────────
  useEffect(() => {
    if (!isPro || !roomId) return;
    const ch = new BroadcastChannel(`tp-${roomId}`);
    bcChannelRef.current = ch;
    let remoteEverConnected = false;
    ch.onmessage = (e) => {
      const { type, cmd } = e.data || {};
      if (type !== "CMD") return;
      if (!remoteEverConnected) { remoteEverConnected = true; wakeAcquire(); }
      setBcRemoteConnected(true);
      handleCmdRef.current?.(cmd, e.data);
    };
    return () => ch.close();
  }, [isPro, roomId, wakeAcquire]);

  // ── WebRTC ──────────────────────────────────────────────────
  const handleRTCMessage = useCallback((e) => {
    if (!isPro) return;
    try {
      const msg = JSON.parse(e.data);
      if (msg.type === "CMD") handleCmdRef.current?.(msg.cmd, msg);
    } catch {}
  }, [isPro]);

  const handleRTCConnected = useCallback((dc, replaceVideoTrack) => {
    if (!isPro) return;
    // Store replaceVideoTrack so the bgMode effect can call it
    replaceVideoTrackRef.current = replaceVideoTrack ?? null;
    setRtcDC(dc);
    setRtcRemoteConnected(true);
    wakeAcquire();
    dc.addEventListener("message", handleRTCMessage);
    dc.addEventListener("close", () => {
      setRtcRemoteConnected(false);
      setRtcDC(null);
      replaceVideoTrackRef.current = null;
    });
    setTimeout(() => pushState(), 300);
  }, [isPro, handleRTCMessage, pushState, wakeAcquire]);

  // ── Scroll to word ──────────────────────────────────────────
  const scrollToWord = useCallback((idx) => {
    const el    = wordRefs.current[idx];
    const stage = stageRef.current;
    if (!el || !stage) return;
    const stageH   = stage.clientHeight;
    const stageTop = stage.getBoundingClientRect().top;
    const elTop    = el.getBoundingClientRect().top;
    const elCenter = elTop - stageTop + el.offsetHeight / 2;
    stage.scrollTo({ top: stage.scrollTop + elCenter - stageH / 2, behavior: "smooth" });
  }, []);

  useEffect(() => { if (current >= 0) scrollToWord(current); }, [current, scrollToWord]);

  // ── Playback engine ─────────────────────────────────────────
  const scheduleNext = useCallback((spd) => {
    clearInterval(timerRef.current);
    const ms = Math.round(60000 / (spd * 12));
    timerRef.current = setInterval(() => {
      setCurrent(prev => {
        const limit = wordsRef.current.length - 1;
        if (prev >= limit) {
          clearInterval(timerRef.current);
          setPlaying(false);
          setStatus("COMPLETE");
          return limit;
        }
        return prev + 1;
      });
    }, ms);
  }, []);

  useEffect(() => { if (playing) scheduleNext(speed); }, [speed]); // eslint-disable-line

  const startWithCountdown = useCallback(() => {
    wakeAcquire();
    let n = 3;
    setCountdown(n);
    cdownRef.current = setInterval(() => {
      n--;
      if (n === 0) {
        clearInterval(cdownRef.current);
        setCountdown(null);
        setCurrent(prev => (prev < 0 ? 0 : prev));
        setPlaying(true);
        setStatus("ROLLING");
        scheduleNext(speedRef.current);
      } else {
        setCountdown(n);
      }
    }, 800);
  }, [scheduleNext, wakeAcquire]);

  const togglePlay = useCallback(() => {
    if (countdown !== null) {
      clearInterval(cdownRef.current);
      setCountdown(null);
      setStatus("READY");
      return;
    }
    if (!playing) {
      wakeAcquire();
      if (currentRef.current >= 0 && statusRef.current === "PAUSED") {
        setPlaying(true);
        setStatus("ROLLING");
        scheduleNext(speedRef.current);
      } else {
        startWithCountdown();
      }
    } else {
      clearInterval(timerRef.current);
      setPlaying(false);
      setStatus("PAUSED");
      wakeRelease();
    }
  }, [playing, countdown, startWithCountdown, scheduleNext, wakeAcquire, wakeRelease]);

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    clearInterval(cdownRef.current);
    setPlaying(false);
    setCountdown(null);
    setCurrent(-1);
    setStatus("READY");
    wakeRelease();
    stageRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [wakeRelease]);

  const jumpTo = useCallback((idx) => {
    setCurrent(idx);
    if (playingRef.current) scheduleNext(speedRef.current);
  }, [scheduleNext]);

  // ── Remote command handler ──────────────────────────────────
  const handleCmd = useCallback((cmd, data) => {
    if (!isPro) return;
    const dispatch = (c) =>
      document.dispatchEvent(new CustomEvent("tp-remote", { detail: { cmd: c } }));
    if (cmd === "REQUEST_STATE") { pushState(); return; }
    if (["TOGGLE_PLAY","PLAY","PAUSE","NEXT","PREV","STOP"].includes(cmd)) { dispatch(cmd); return; }
    if (cmd === "SET_SPEED" && data.value) { setSpeed(data.value); return; }
    if (cmd === "JUMP_TO" && data.index != null) { jumpTo(data.index); return; } // ← NEW
    if (cmd === "CUE") setActiveCue({ label: data.label, bg: data.bg, accent: data.accent, icon: data.icon ?? "standby" });
  }, [isPro, pushState, jumpTo]); // ← jumpTo added to deps

  handleCmdRef.current = handleCmd;

  // ── Custom event listener (remote commands) ─────────────────
  useEffect(() => {
    const handler = (e) => {
      const { cmd } = e.detail;
      if (cmd === "TOGGLE_PLAY") {
        togglePlay();
      } else if (cmd === "PLAY") {
        if (!playingRef.current) {
          wakeAcquire();
          if (currentRef.current >= 0 && statusRef.current === "PAUSED") {
            setPlaying(true); setStatus("ROLLING"); scheduleNext(speedRef.current);
          } else {
            startWithCountdown();
          }
        }
      } else if (cmd === "PAUSE") {
        if (playingRef.current) {
          clearInterval(timerRef.current);
          setPlaying(false); setStatus("PAUSED"); wakeRelease();
        }
      } else if (cmd === "STOP") {
        stop();
      } else if (cmd === "NEXT") {
        setCurrent(prev => {
          const n = Math.min(prev < 0 ? 0 : prev + 1, wordsRef.current.length - 1);
          if (playingRef.current) scheduleNext(speedRef.current);
          return n;
        });
      } else if (cmd === "PREV") {
        setCurrent(prev => {
          const n = Math.max(prev - 1, 0);
          if (playingRef.current) scheduleNext(speedRef.current);
          return n;
        });
      }
    };
    document.addEventListener("tp-remote", handler);
    return () => document.removeEventListener("tp-remote", handler);
  }, [togglePlay, stop, scheduleNext, startWithCountdown, wakeAcquire, wakeRelease, playing]);

  // ── Keyboard shortcuts ──────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (editOpen || helpOpen || showQR || showClips) return;
      if (e.code === "Space")  { e.preventDefault(); togglePlay(); }
      if (e.code === "Escape") {
        if (customCdActive) { stopCustomCountdown(); return; }
        if (isPro && activeCue) { setActiveCue(null); return; }
        stop();
      }
      if (e.code === "KeyF" && !e.metaKey && !e.ctrlKey) toggleFullscreen();
      if (e.code === "Slash" && e.shiftKey) { e.preventDefault(); setHelpOpen(true); }
      if (e.code === "ArrowRight" || e.code === "ArrowDown") {
        e.preventDefault();
        setCurrent(prev => {
          const n = Math.min(prev < 0 ? 0 : prev + 1, words.length - 1);
          if (playingRef.current) scheduleNext(speedRef.current);
          return n;
        });
      }
      if (e.code === "ArrowLeft" || e.code === "ArrowUp") {
        e.preventDefault();
        setCurrent(prev => {
          const n = Math.max(prev - 1, 0);
          if (playingRef.current) scheduleNext(speedRef.current);
          return n;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editOpen, helpOpen, showQR, showClips, activeCue, customCdActive, togglePlay, stop, scheduleNext, words.length, isPro]); // eslint-disable-line

  // ── Fullscreen ──────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) rootRef.current?.requestFullscreen();
    else document.exitFullscreen();
  }, []);

  // ── Background stream helpers ───────────────────────────────
  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const toggleCamera = useCallback(async () => {
    if (bgMode === "camera") { stopStream(); setBgMode("off"); showToast("Camera off"); return; }
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      setBgMode("camera"); showToast("Camera background on");
    } catch { showToast("Camera access denied"); setBgMode("off"); }
  }, [bgMode, stopStream, showToast]);

  const toggleScreen = useCallback(async () => {
    if (bgMode === "screen") { stopStream(); setBgMode("off"); showToast("Screen share off"); return; }
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      stream.getVideoTracks()[0].addEventListener("ended", () => { setBgMode("off"); stopStream(); });
      setBgMode("screen"); showToast("Screen background on");
    } catch { showToast("Screen share cancelled"); setBgMode("off"); }
  }, [bgMode, stopStream, showToast]);

  // ── Recording ───────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const camStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      camRecStreamRef.current = camStream;
      recChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";
      const mr = new MediaRecorder(camStream, { mimeType });
      mr.ondataavailable = e => { if (e.data.size > 0) recChunksRef.current.push(e.data); };
      mr.onstop = () => {
        camStream.getTracks().forEach(t => t.stop());
        camRecStreamRef.current = null;
        const blob = new Blob(recChunksRef.current, { type: mimeType });
        const url  = URL.createObjectURL(blob);
        const dur  = recSecondsRef.current;
        const ext  = mimeType.includes("mp4") ? "mp4" : "webm";
        const name = `take-${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}.${ext}`;
        setClips(prev => [...prev, { name, url, duration: dur, sizeMB: (blob.size / 1024 / 1024).toFixed(1), mimeType }]);
        showToast(`Recording saved — ${formatTime(dur)}`);
        setShowClips(true);
        wakeAcquire();
      };
      mr.start(500);
      mediaRecRef.current = mr;
      recSecondsRef.current = 0;
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        recSecondsRef.current += 1;
        setRecSeconds(recSecondsRef.current);
      }, 1000);
      setRecording(true);
      showToast("Camera recording started");
    } catch { showToast("Camera/mic access denied"); }
  }, [showToast, wakeAcquire]);

  const stopRecording = useCallback(() => {
    if (mediaRecRef.current?.state !== "inactive") mediaRecRef.current.stop();
    clearInterval(recTimerRef.current);
    setRecording(false);
  }, []);

  const toggleRecording = useCallback(() => {
    recording ? stopRecording() : startRecording();
  }, [recording, startRecording, stopRecording]);

  const downloadClip = useCallback((clip) => {
    const a = document.createElement("a");
    a.href = clip.url; a.download = clip.name; a.click();
  }, []);

  const downloadAllClips = useCallback(() => clips.forEach(downloadClip), [clips, downloadClip]);

  const deleteClip = useCallback((idx) => {
    setClips(prev => { URL.revokeObjectURL(prev[idx].url); return prev.filter((_, i) => i !== idx); });
  }, []);

  // ── Custom Countdown ────────────────────────────────────────
  const startCustomCountdown = useCallback((seconds) => {
    clearInterval(customCdRef.current);
    setCustomCdRemaining(seconds);
    setCustomCdActive(true);
    setCustomCdOpen(false);
    customCdRef.current = setInterval(() => {
      setCustomCdRemaining(prev => {
        if (prev <= 1) { clearInterval(customCdRef.current); setCustomCdActive(false); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const stopCustomCountdown = useCallback(() => {
    clearInterval(customCdRef.current);
    setCustomCdActive(false);
    setCustomCdRemaining(0);
  }, []);

  const formatCustomCd = useCallback((secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : String(secs);
  }, []);

  // ── Cleanup ─────────────────────────────────────────────────
  useEffect(() => () => {
    clearInterval(timerRef.current); clearInterval(cdownRef.current);
    clearTimeout(pushStateTimer.current); clearInterval(recTimerRef.current);
    clearInterval(customCdRef.current);
    stopStream();
    camRecStreamRef.current?.getTracks().forEach(t => t.stop());
    if (mediaRecRef.current?.state !== "inactive") mediaRecRef.current?.stop();
  }, [stopStream]);

  // ── Script application ──────────────────────────────────────
  const doApplyScript = useCallback((raw) => {
    clearInterval(timerRef.current); clearInterval(cdownRef.current);
    setPlaying(false); setCountdown(null); setCurrent(-1); setStatus("READY");
    const newTokens = parseScript(raw);
    setTokens(newTokens); setWords(tokensToWords(newTokens)); setActiveNotes([]);
    setEditOpen(false); wakeRelease(); savePrefs({ script: raw });
    if (stageRef.current) stageRef.current.scrollTo({ top: 0 });
  }, [wakeRelease]);

  const applyScript = useCallback((rawOverride) => {
    const raw    = rawOverride ?? draftScript;
    const parsed = parseWords(raw);
    if (!parsed.length) return;
    const oldSecs = splitIntoSections(tokensToRaw(tokens));
    const newSecs = splitIntoSections(raw);
    const hasDiff = parsed.length > 300 && newSecs.some((s) => {
      const old = oldSecs.find(o => (o.heading ?? "__ROOT__") === (s.heading ?? "__ROOT__"));
      return !old || old.body !== s.body;
    });
    if (hasDiff && tokens.length > 0) {
      setPendingScript(raw); setEditOpen(false); setDiffOpen(true); return;
    }
    doApplyScript(raw);
  }, [draftScript, tokens, doApplyScript]);

  const handleDiffApply = useCallback((mergedRaw) => {
    doApplyScript(mergedRaw); setDiffOpen(false);
  }, [doApplyScript]);

  // ── Presenter notes ─────────────────────────────────────────
  useEffect(() => {
    if (current < 0) { setActiveNotes([]); return; }
    const notes = notesAtWord(tokens, current);
    if (notes.length) setActiveNotes(notes);
  }, [current, tokens]);

  // ── Edit modal ──────────────────────────────────────────────
  const openEdit = useCallback(() => {
    setDraftScript(tokensToRaw(tokens)); setDraftCursor(null); setEditOpen(true);
  }, [tokens]);

  useEffect(() => {
    if (editOpen && textareaRef.current) {
      const pos = draftCursor ?? textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(pos, pos); textareaRef.current.focus();
    }
  }, [editOpen]); // eslint-disable-line

  const cycleFont = useCallback(() => {
    setFont(f => FONTS[(FONTS.indexOf(f) + 1) % FONTS.length]);
  }, []);

  const shareScript = useCallback(async () => {
    const text = words.join(" ");
    if (navigator.share) {
      try { await navigator.share({ title: "Teleprompter Script", text }); }
      catch (err) { if (err.name !== "AbortError") showToast("Share failed"); }
    } else {
      try { await navigator.clipboard.writeText(text); showToast("Script copied to clipboard"); }
      catch { showToast("Copy failed"); }
    }
  }, [words, showToast]);

  // ── Derived values ──────────────────────────────────────────
  const progress       = current < 0 ? 0 : ((current + 1) / words.length * 100);
  const wordCount      = current < 0 ? 0 : current + 1;
  const isRunning      = playing || countdown !== null;
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const estRemaining   = useMemo(() => estimateReadTime(words.length - Math.max(0, current + 1), speed), [words.length, current, speed]);
  const estTotal       = useMemo(() => estimateReadTime(words.length, speed), [words.length, speed]);

  const getWordClass = useCallback((i) => {
    if (i < current)   return "tp-word done";
    if (i === current) return "tp-word current";
    const dist = i - current;
    if (dist <= 2) return "tp-word near";
    if (dist <= 6) return "tp-word upcoming";
    return "tp-word";
  }, [current]);

  // ── Context value ───────────────────────────────────────────
  const value = {
    isPro, onLogout, proAction,
    tokens, words, sections, draftScript, setDraftScript, draftCursor, setDraftCursor,
    pendingScript, diffOpen, setDiffOpen,
    current, playing, speed, setSpeed, status, countdown, isRunning,
    togglePlay, stop, jumpTo,
    fontSize, setFontSize, mirrored, setMirrored, font, setFont, cycleFont,
    bgMode, bgOpacity, setBgOpacity, brightness, setBrightness, isFullscreen,
    toggleFullscreen,
    toggleCamera, toggleScreen,
    recording, recSeconds, clips, toggleRecording, downloadClip, downloadAllClips, deleteClip,
    roomId, remoteConnected, rtcRemoteConnected, bcRemoteConnected,
    handleRTCConnected, initialOffer,
    editOpen, setEditOpen, openEdit, helpOpen, setHelpOpen,
    showQR, setShowQR, showClips, setShowClips,
    showCuePanel, setShowCuePanel, showSections, setShowSections,
    showNotes, setShowNotes,
    activeCue, setActiveCue,
    activeNotes,
    customCdOpen, setCustomCdOpen, customCdInput, setCustomCdInput,
    customCdActive, customCdRemaining, startCustomCountdown, stopCustomCountdown, formatCustomCd,
    wakeActive, wakeSupported, wakeToggle,
    toastMsg, toastVisible, showToast,
    applyScript, handleDiffApply,
    shareScript, canNativeShare,
    progress, wordCount, estRemaining, estTotal,
    getWordClass,
    rootRef, stageRef, wordRefs, textareaRef, videoRef,
  };

  return (
    <TeleprompterContext.Provider value={value}>
      {children}
    </TeleprompterContext.Provider>
  );
}


// ─────────────────────────────────────────────────────────────
//  GlobalStyles
// ─────────────────────────────────────────────────────────────
function GlobalStyles() {
  return (
    <style>{`
      .tp-cue-banner { position:relative; display:flex; align-items:center; gap:10px; width:100%; min-height:44px; padding:0 14px 0 0; background:color-mix(in srgb,var(--cue-bg,#0c0c0c) 92%,transparent); border-bottom:1px solid color-mix(in srgb,var(--cue-accent,#d4af37) 35%,transparent); overflow:hidden; animation:tp-cue-banner-in .2s cubic-bezier(.34,1.46,.64,1) both; flex-shrink:0; z-index:10; }
      .tp-cue-banner--exit { animation:tp-cue-banner-out .28s ease-in both; }
      @keyframes tp-cue-banner-in  { from{opacity:0;transform:translateY(-110%)} to{opacity:1;transform:translateY(0)} }
      @keyframes tp-cue-banner-out { from{opacity:1;max-height:60px} to{opacity:0;max-height:0;padding:0;border:none} }
      .tp-cue-banner__stripe { width:4px; align-self:stretch; flex-shrink:0; }
      .tp-cue-banner__icon   { display:flex; align-items:center; flex-shrink:0; padding:10px 0; }
      .tp-cue-banner__label  { flex:1; font-family:'DM Serif Display','Georgia',serif; font-size:18px; font-weight:700; letter-spacing:.03em; line-height:1.1; padding:10px 0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .tp-cue-banner__countdown { font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:.14em; opacity:.7; flex-shrink:0; }
      .tp-cue-banner__close  { background:none; border:none; color:var(--cue-accent,#d4af37); cursor:pointer; opacity:.6; display:flex; align-items:center; padding:4px; flex-shrink:0; }
      .tp-cue-banner__close:hover { opacity:1; }
      .tp-cue-banner__progress { position:absolute; bottom:0; left:0; right:0; height:2px; background:rgba(255,255,255,.08); }
      .tp-cue-banner__progress-fill { height:100%; transition:width .1s linear; }

      .tp-custom-cd-overlay { position:fixed; inset:0; z-index:200; background:rgba(0,0,0,.93); display:flex; flex-direction:column; align-items:center; justify-content:center; animation:tp-cue-banner-in .25s cubic-bezier(.34,1.46,.64,1) both; cursor:pointer; user-select:none; }
      .tp-custom-cd-label   { font-size:9px; letter-spacing:.3em; text-transform:uppercase; color:rgba(212,175,55,.45); font-family:'IBM Plex Mono',monospace; margin-bottom:20px; }
      .tp-custom-cd-number  { font-family:'DM Serif Display','Georgia',serif; font-size:clamp(80px,22vw,220px); font-weight:700; color:var(--g,#d4af37); line-height:1; letter-spacing:-.02em; transition:color .3s; }
      .tp-custom-cd-number.urgent { color:#ff7043; animation:tp-cd-pulse .5s ease-in-out infinite alternate; }
      .tp-custom-cd-unit    { font-size:11px; color:rgba(212,175,55,.35); font-family:'IBM Plex Mono',monospace; margin-top:14px; letter-spacing:.25em; text-transform:uppercase; }
      .tp-custom-cd-hint    { font-size:10px; color:rgba(255,255,255,.18); font-family:'IBM Plex Mono',monospace; margin-top:52px; letter-spacing:.15em; }
      .tp-custom-cd-stop    { margin-top:28px; padding:8px 24px; border-radius:8px; border:1px solid rgba(255,100,80,.4); background:rgba(255,100,80,.1); color:#ff7043; font-family:'IBM Plex Mono',monospace; font-size:11px; cursor:pointer; letter-spacing:.1em; transition:background .15s; }
      .tp-custom-cd-stop:hover { background:rgba(255,100,80,.22); }
      @keyframes tp-cd-pulse { from{opacity:1} to{opacity:.55} }

      .tp-scroll-stage { cursor:grab; }
      .tp-scroll-stage:active { cursor:grabbing; }
    `}</style>
  );
}


// ─────────────────────────────────────────────────────────────
//  CueBanner
// ─────────────────────────────────────────────────────────────
function CueBanner({ cue, onDismiss }) {
  const [progress, setProgress] = useState(1);
  const [exiting,  setExiting]  = useState(false);
  const rafRef   = useRef(null);
  const startRef = useRef(null);
  const doneRef  = useRef(false);

  const dismiss = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    cancelAnimationFrame(rafRef.current);
    setExiting(true);
    setTimeout(onDismiss, 280);
  }, [onDismiss]);

  useEffect(() => {
    if (!cue) return;
    doneRef.current = false;
    setExiting(false);
    setProgress(1);
    startRef.current = performance.now();
    const tick = (now) => {
      const frac = Math.max(0, 1 - (now - startRef.current) / CUE_DURATION);
      setProgress(frac);
      if (frac > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [cue, dismiss]);

  if (!cue) return null;
  const secsLeft = Math.ceil((CUE_DURATION / 1000) * progress);

  return (
    <div
      className={`tp-cue-banner${exiting ? " tp-cue-banner--exit" : ""}`}
      style={{ "--cue-accent": cue.accent, "--cue-bg": cue.bg }}
      role="status" aria-live="polite"
    >
      <div className="tp-cue-banner__stripe" style={{ background: cue.accent }} />
      <span className="tp-cue-banner__icon" style={{ color: cue.accent }}>
        <CueLabelIcon name={cue.icon} size={18} />
      </span>
      <span className="tp-cue-banner__label" style={{ color: cue.accent }}>{cue.label}</span>
      <span className="tp-cue-banner__countdown" style={{ color: cue.accent }}>{secsLeft}s</span>
      <button className="tp-cue-banner__close" onClick={dismiss} aria-label="Dismiss cue">
        <CloseIcon size={14} />
      </button>
      <div className="tp-cue-banner__progress">
        <div className="tp-cue-banner__progress-fill" style={{ width: `${progress * 100}%`, background: cue.accent }} />
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  TopBar
// ─────────────────────────────────────────────────────────────
function TopBar() {
  const {
    isPro, onLogout, showToast,
    remoteConnected, rtcRemoteConnected,
    wakeActive, wakeSupported, wakeToggle,
    canNativeShare, shareScript,
    showQR, setShowQR, proAction,
    showCuePanel, setShowCuePanel,
    sections, showSections, setShowSections,
    tokens, showNotes, setShowNotes,
    helpOpen, setHelpOpen,
    isFullscreen, toggleFullscreen,
  } = useTeleprompter();

  return (
    <div className="tp-topbar">
      <div className="tp-brand"><a href="/">Teleprompter</a></div>
      <div className="tp-header-right">

        <div
          style={{
            display:"flex", alignItems:"center", gap:5,
            background: isPro ? "rgba(212,175,55,.08)" : "rgba(100,100,100,.08)",
            border:`1px solid ${isPro ? "rgba(212,175,55,.25)" : "rgba(100,100,100,.25)"}`,
            borderRadius:20, padding:"4px 10px 4px 8px", fontSize:9,
            letterSpacing:".15em", textTransform:"uppercase",
            fontFamily:"'IBM Plex Mono',monospace",
            color: isPro ? "var(--g)" : "#888",
            cursor: isPro ? "pointer" : "default",
          }}
          onClick={() => { if (isPro) { onLogout?.(); showToast("Signed out"); } }}
        >
          <span style={{ width:6, height:6, borderRadius:"50%", background: isPro ? "var(--g)" : "#888", display:"inline-block" }} />
          {isPro ? "Pro" : "Free"}
        </div>

        {isPro && remoteConnected && (
          <div className="tp-remote-pill">
            <span className="tp-remote-dot" />
            {rtcRemoteConnected ? "WebRTC" : "Remote"}
          </div>
        )}

        <button
          className={`tp-wake-pill${wakeActive ? " active" : ""}${!wakeSupported ? " unsupported" : ""}`}
          title={wakeSupported ? (wakeActive ? "Screen on" : "Keep screen awake") : "Not supported"}
          onClick={wakeSupported ? wakeToggle : undefined}
        >
          <span className="tp-wake-dot" />{wakeActive ? "Live" : "Keep on"}
        </button>

        <button className="tp-btn" title={canNativeShare ? "Share script" : "Copy script"} onClick={shareScript}>
          {canNativeShare ? <ShareIcon size={15}/> : <CopyClipboardIcon size={15}/>}
        </button>

        <button
          className={`tp-btn${showQR ? " active" : ""}`}
          title="Cross-device remote (WebRTC)"
          onClick={proAction(() => setShowQR(true))}
        >
          <QRIcon />
        </button>

        <button
          className={`tp-btn${showCuePanel ? " active" : ""}`}
          title="Live Cue Panel"
          onClick={proAction(() => setShowCuePanel(v => !v))}
        >
          <GridIcon size={15}/>
        </button>

        {sections.length > 0 && (
          <button
            className={`tp-btn${showSections ? " active" : ""}`}
            title="Jump to Section"
            onClick={() => setShowSections(v => !v)}
          >
            <SectionsIcon size={15}/>
          </button>
        )}

        {tokens.some(t => t.type === "note") && (
          <button
            className={`tp-btn${showNotes ? " active" : ""}`}
            title="Presenter Notes"
            onClick={() => setShowNotes(v => !v)}
          >
            <NotesIcon size={15}/>
          </button>
        )}

        <button className="tp-btn" title="Keyboard shortcuts" onClick={() => setHelpOpen(true)}>
          <HelpIcon size={15}/>
        </button>

        <button
          className={`tp-btn${isFullscreen ? " active" : ""}`}
          title={isFullscreen ? "Exit Fullscreen (F)" : "Fullscreen (F)"}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <FullscreenExitIcon size={15}/> : <FullscreenEnterIcon size={15}/>}
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  PrompterStage — with drag-to-scroll
// ─────────────────────────────────────────────────────────────
function PrompterStage() {
  const {
    isPro,
    bgMode, bgOpacity, brightness, countdown,
    activeCue, setActiveCue,
    words, getWordClass, jumpTo,
    font, fontSize,
    stageRef, wordRefs, videoRef,
  } = useTeleprompter();

  // Drag-to-scroll — shared ref so the onClick guard works without stale closure
  const dragRef = useRef({ active: false, startY: 0, startScroll: 0, moved: false });

  const onPointerDown = useCallback((e) => {
    // Only handle primary button on mouse; all touches
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const stage = stageRef.current;
    if (!stage) return;
    dragRef.current = { active: true, startY: e.clientY, startScroll: stage.scrollTop, moved: false };
    stage.setPointerCapture(e.pointerId);
    e.preventDefault();
  }, [stageRef]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dy = e.clientY - d.startY;
    if (Math.abs(dy) > 4) d.moved = true;
    if (d.moved && stageRef.current) {
      stageRef.current.scrollTop = d.startScroll - dy;
    }
  }, [stageRef]);

  const onPointerUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  return (
    <div className="tp-prompt-wrap">
      <video
        ref={videoRef}
        className={`tp-bg-video${bgMode === "off" ? " tp-hidden" : ""}`}
        autoPlay muted playsInline
      />
      {bgMode !== "off" && (
        <div className="tp-bg-dim" style={{ background: `rgba(12,12,12,${bgOpacity})` }} />
      )}
      <div className="tp-fade-top" />
      <div className="tp-focus-zone">
        <div className="tp-focus-bracket-left" />
        <div className="tp-focus-line" />
        <div className="tp-focus-bracket-right" />
      </div>
      <div className="tp-fade-bottom" />

      {countdown !== null && (
        <div className="tp-countdown">
          <div className="tp-countdown-num" key={countdown}>{countdown}</div>
        </div>
      )}

      {isPro && activeCue && (
        <CueBanner cue={activeCue} onDismiss={() => setActiveCue(null)} />
      )}

      <div
        className="tp-scroll-stage"
        ref={stageRef}
        style={{
          filter: `brightness(${brightness}%)`,
          touchAction: "none",   // hand all touch input to Pointer Events
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className={`tp-text-body ${font}`} style={{ fontSize: `${fontSize}px` }}>
          {words.map((w, i) => (
            <span
              key={i}
              className={getWordClass(i)}
              ref={el => { if (el) wordRefs.current[i] = el; else delete wordRefs.current[i]; }}
              onClick={() => { if (!dragRef.current.moved) jumpTo(i); }}
            >
              {w + " "}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  FooterControls
// ─────────────────────────────────────────────────────────────
function FooterControls() {
  const {
    isPro, proAction,
    status, wordCount, words, progress, current,
    estTotal, estRemaining,
    recording, recSeconds,
    customCdActive, customCdRemaining, formatCustomCd,
    togglePlay, stop, isRunning,
    speed, setSpeed,
    fontSize, setFontSize,
    brightness, setBrightness,
    bgMode, setBgOpacity, bgOpacity,
    toggleCamera, toggleScreen,
    toggleRecording, clips, setShowClips,
    font, cycleFont,
    mirrored, setMirrored,
    openEdit,
  } = useTeleprompter();

  return (
    <div className="tp-footer">
      <div className="tp-status-left">
        <span>{status}</span>
        <span>{wordCount} / {words.length}</span>
        <div className="tp-progress-pill">
          <div className="tp-progress-fill" style={{ width: `${progress.toFixed(1)}%` }} />
        </div>

        {current < 0 && estTotal && (
          <span style={{ color:"var(--muted)", fontSize:10, letterSpacing:".1em" }}>
            ~{formatTime(estTotal)}
          </span>
        )}
        {current >= 0 && estRemaining != null && (
          <span style={{ color: estRemaining < 30 ? "#ff7043" : "var(--muted)", fontSize:10, letterSpacing:".1em" }}>
            -{formatTime(estRemaining)}
          </span>
        )}

        {isPro && recording && (
          <div className="tp-rec-indicator">
            <div className="tp-rec-dot" />REC {formatTime(recSeconds)}
          </div>
        )}

        {isPro && customCdActive && (
          <div style={{ display:"flex", alignItems:"center", gap:5, background:"rgba(212,175,55,.1)", border:"1px solid rgba(212,175,55,.3)", borderRadius:10, padding:"2px 9px", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, color: customCdRemaining <= 10 ? "#ff7043" : "var(--g)", letterSpacing:".08em" }}>
            <svg viewBox="0 0 24 24" width={11} height={11} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <polyline points="12 7 12 12 15 15"/>
              <line x1="12" y1="3" x2="12" y2="1"/>
              <line x1="8" y1="3.5" x2="7" y2="1.9"/>
              <line x1="16" y1="3.5" x2="17" y2="1.9"/>
            </svg>
            {formatCustomCd(customCdRemaining)}
          </div>
        )}
      </div>

      <div className="tp-controls">
        <button className="tp-btn" title="Stop & Reset (Esc)" onClick={stop}>
          <StopIcon size={15}/>
        </button>

        <button
          className={`tp-btn lg${isRunning ? " accent" : ""}`}
          title={isRunning ? "Pause (Space)" : "Play (Space)"}
          onClick={togglePlay}
        >
          {isRunning ? <PauseIcon size={18}/> : <PlayIcon size={18}/>}
        </button>

        <div className="tp-ctrl-group" style={{ gap:5 }}>
          <span className="tp-ctrl-label">Speed</span>
          {SPEED_PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => setSpeed(p.value)}
              style={{
                padding:"2px 7px", borderRadius:5, border:"none",
                background: speed === p.value ? "var(--g)" : "transparent",
                color: speed === p.value ? "#0c0c0c" : "var(--muted)",
                fontFamily:"'IBM Plex Mono',monospace", fontSize:9,
                letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer",
                fontWeight: speed === p.value ? 700 : 400,
                transition:"background .12s,color .12s",
              }}
            >
              {p.label}
            </button>
          ))}
          <input
            type="range" min="1" max="10" step="1"
            value={speed} className="tp-range" style={{ width:52 }}
            onChange={e => setSpeed(parseInt(e.target.value))}
          />
          <span className="tp-ctrl-val" style={{ minWidth:18 }}>{speed}</span>
        </div>

        <div className="tp-ctrl-group">
          <span className="tp-ctrl-label">Size</span>
          <input
            type="range" min="20" max="72" step="2"
            value={fontSize} className="tp-range"
            onChange={e => setFontSize(parseInt(e.target.value))}
          />
          <span className="tp-ctrl-val">{fontSize}</span>
        </div>

        <div className="tp-ctrl-group" title="Teleprompter text brightness">
          <span className="tp-ctrl-label">Bright</span>
          <input
            type="range" min="40" max="160" step="5"
            value={brightness} className="tp-range" style={{ width:60 }}
            onChange={e => setBrightness(parseInt(e.target.value))}
          />
          <span className="tp-ctrl-val" style={{ minWidth:28 }}>{brightness}%</span>
        </div>

        <div className="tp-bg-group" title="Background source">
          <button
            className={`tp-bg-group-btn${bgMode === "camera" ? " active" : ""}`}
            onClick={proAction(toggleCamera)}
            title="Camera background"
          >
            <CamIcon /><span>CAM</span>
          </button>
          <button
            className={`tp-bg-group-btn${bgMode === "screen" ? " active" : ""}`}
            onClick={proAction(toggleScreen)}
            title="Screen share background"
          >
            <ScrIcon /><span>SCR</span>
          </button>
        </div>

        {isPro && bgMode !== "off" && (
          <div className="tp-ctrl-group">
            <span className="tp-ctrl-label">Dim</span>
            <input
              type="range" min="0" max="0.95" step="0.05"
              value={bgOpacity} className="tp-range" style={{ width:60 }}
              onChange={e => setBgOpacity(parseFloat(e.target.value))}
            />
            <span className="tp-ctrl-val" style={{ minWidth:24 }}>{Math.round(bgOpacity * 100)}%</span>
          </div>
        )}

        <div className="tp-rec-group">
          <button
            className={`tp-rec-main-btn${recording ? " recording" : ""}`}
            onClick={proAction(toggleRecording)}
            title="Record from camera"
          >
            {recording ? <StopRecIcon /> : <RecIcon recording={recording} />}
            {recording ? "Stop" : "Rec"}
          </button>
          {recording && <div className="tp-rec-timer">{formatTime(recSeconds)}</div>}
          {clips.length > 0 && (
            <button className="tp-rec-download-btn" onClick={proAction(() => setShowClips(true))}>
              <DlIcon />
            </button>
          )}
        </div>

        <button
          className="tp-btn"
          title="Cycle font"
          onClick={cycleFont}
          style={{
            fontFamily: font === "serif" ? "'DM Serif Display',serif" : font === "mono" ? "'IBM Plex Mono',monospace" : "'Inter',sans-serif",
            fontWeight:500, fontSize:13,
            borderColor: font !== "serif" ? "var(--g)" : undefined,
            color: font !== "serif" ? "var(--g)" : undefined,
          }}
        >
          {FONT_LABELS[font]}
        </button>

        <button
          className={`tp-btn${mirrored ? " active" : ""}`}
          title="Mirror mode"
          onClick={() => setMirrored(m => !m)}
        >
          <MirrorIcon size={15}/>
        </button>

        <button className="tp-btn" title="Edit Script" onClick={openEdit}>
          <EditIcon size={15}/>
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  CuePanelDropdown
// ─────────────────────────────────────────────────────────────
function CuePanelDropdown() {
  const {
    isPro, showCuePanel, setShowCuePanel,
    customCdOpen, setCustomCdOpen,
    customCdInput, setCustomCdInput,
    customCdActive, customCdRemaining,
    startCustomCountdown, stopCustomCountdown, formatCustomCd,
  } = useTeleprompter();

  if (!isPro || !showCuePanel) return null;

  return (
    <div style={{ position:"absolute", top:56, right:12, zIndex:90, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding:"14px 14px 16px", width:300, boxShadow:"0 8px 32px rgba(0,0,0,.6)", animation:"tp-cue-slide-in .22s cubic-bezier(.34,1.46,.64,1) both" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <span style={{ fontSize:9, letterSpacing:".2em", textTransform:"uppercase", color:"var(--g)", fontFamily:"'IBM Plex Mono',monospace" }}>Live Cues</span>
        <button onClick={() => setShowCuePanel(false)} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:4 }}>
          <CloseIcon size={14}/>
        </button>
      </div>
      <CustomCountdownControl
        customCdOpen={customCdOpen}
        setCustomCdOpen={setCustomCdOpen}
        customCdInput={customCdInput}
        setCustomCdInput={setCustomCdInput}
        customCdActive={customCdActive}
        customCdRemaining={customCdRemaining}
        startCustomCountdown={startCustomCountdown}
        stopCustomCountdown={stopCustomCountdown}
        formatCustomCd={formatCustomCd}
      />
    </div>
  );
}

function CustomCountdownControl({
  customCdOpen, setCustomCdOpen,
  customCdInput, setCustomCdInput,
  customCdActive, customCdRemaining,
  startCustomCountdown, stopCustomCountdown,
  formatCustomCd,
}) {
  return (
    <div style={{ marginBottom:12, padding:"10px 12px", background:"rgba(212,175,55,.06)", border:"1px solid rgba(212,175,55,.2)", borderRadius:10 }}>
      <div style={{ fontSize:9, letterSpacing:".18em", textTransform:"uppercase", color:"var(--g)", fontFamily:"'IBM Plex Mono',monospace", marginBottom:8 }}>Custom Countdown</div>

      {customCdOpen ? (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {["10","30","60","90","120","300"].map(s => (
              <button
                key={s}
                onClick={() => setCustomCdInput(s)}
                style={{ padding:"3px 9px", borderRadius:6, border:`1px solid ${customCdInput === s ? "var(--g)" : "var(--border)"}`, background: customCdInput === s ? "rgba(212,175,55,.15)" : "transparent", color: customCdInput === s ? "var(--g)" : "var(--muted)", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, cursor:"pointer", transition:"border-color .12s,background .12s" }}
              >
                {parseInt(s) >= 60 ? `${parseInt(s)/60}m` : `${s}s`}
              </button>
            ))}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <input
              type="number" min="1" max="3600"
              value={customCdInput}
              onChange={e => setCustomCdInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { const s = parseInt(customCdInput); if (s > 0) startCustomCountdown(s); } }}
              style={{ flex:1, background:"var(--ctrl-bg)", border:"1px solid var(--border)", borderRadius:7, color:"var(--text)", fontFamily:"'IBM Plex Mono',monospace", fontSize:13, padding:"5px 9px", outline:"none", width:0 }}
              placeholder="seconds"
              autoFocus
            />
            <span style={{ fontSize:10, color:"var(--muted)", fontFamily:"'IBM Plex Mono',monospace" }}>sec</span>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button
              onClick={() => { const s = parseInt(customCdInput); if (s > 0) startCustomCountdown(s); }}
              style={{ flex:1, padding:"7px 0", borderRadius:8, border:"1px solid rgba(212,175,55,.5)", background:"rgba(212,175,55,.12)", color:"var(--g)", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, fontWeight:700, cursor:"pointer", letterSpacing:".08em", transition:"background .12s" }}
              onMouseOver={e => e.currentTarget.style.background="rgba(212,175,55,.22)"}
              onMouseOut={e => e.currentTarget.style.background="rgba(212,175,55,.12)"}
            >START</button>
            <button
              onClick={() => setCustomCdOpen(false)}
              style={{ padding:"7px 12px", borderRadius:8, border:"1px solid var(--border)", background:"transparent", color:"var(--muted)", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, cursor:"pointer" }}
            >Cancel</button>
          </div>
        </div>

      ) : customCdActive ? (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:22, fontWeight:700, color: customCdRemaining <= 10 ? "#ff7043" : "var(--g)", letterSpacing:".04em", transition:"color .3s" }}>
            {formatCustomCd(customCdRemaining)}
          </span>
          <button
            onClick={stopCustomCountdown}
            style={{ padding:"5px 12px", borderRadius:7, border:"1px solid rgba(255,100,80,.4)", background:"rgba(255,100,80,.1)", color:"#ff7043", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, cursor:"pointer" }}
          >Stop</button>
        </div>

      ) : (
        <button
          onClick={() => setCustomCdOpen(true)}
          style={{ width:"100%", padding:"7px 0", borderRadius:8, border:"1px solid rgba(212,175,55,.3)", background:"transparent", color:"var(--g)", fontFamily:"'IBM Plex Mono',monospace", fontSize:10, cursor:"pointer", letterSpacing:".1em", transition:"background .12s" }}
          onMouseOver={e => e.currentTarget.style.background="rgba(212,175,55,.07)"}
          onMouseOut={e => e.currentTarget.style.background="transparent"}
        >+ Set Timer</button>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  SectionPanel
// ─────────────────────────────────────────────────────────────
function SectionPanel() {
  const {
    isPro, showSections, setShowSections, showCuePanel,
    sections, current, jumpTo, showToast,
  } = useTeleprompter();

  if (!showSections || !sections.length) return null;

  return (
    <div style={{ position:"absolute", top:56, right: isPro && showCuePanel ? 328 : 12, zIndex:90, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding:"14px 14px 16px", minWidth:220, maxWidth:300, boxShadow:"0 8px 32px rgba(0,0,0,.6)", animation:"tp-cue-slide-in .22s cubic-bezier(.34,1.46,.64,1) both" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <span style={{ fontSize:9, letterSpacing:".2em", textTransform:"uppercase", color:"var(--g)", fontFamily:"'IBM Plex Mono',monospace" }}>Jump to Section</span>
        <button onClick={() => setShowSections(false)} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:4 }}>
          <CloseIcon size={14}/>
        </button>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:5, maxHeight:260, overflowY:"auto" }}>
        {sections.map((sec, i) => {
          const isActive = current >= sec.wordIndex && (sections[i+1] == null || current < sections[i+1].wordIndex);
          return (
            <button
              key={i}
              onClick={() => { jumpTo(sec.wordIndex); setShowSections(false); showToast(sec.label); }}
              style={{ padding:"8px 12px", borderRadius:8, textAlign:"left", background: isActive ? "rgba(212,175,55,.12)" : "var(--ctrl-bg)", border:`1px solid ${isActive ? "rgba(212,175,55,.4)" : "var(--border)"}`, color:"var(--text)", fontFamily:"'IBM Plex Mono',monospace", fontSize:11, cursor:"pointer" }}
            >
              <span style={{ color:"var(--g)", marginRight:8 }}>{String(i+1).padStart(2,"0")}</span>
              {sec.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  PresenterNotes
// ─────────────────────────────────────────────────────────────
function PresenterNotes() {
  const { showNotes, activeNotes } = useTeleprompter();

  if (!showNotes || !activeNotes.length) return null;

  return (
    <div style={{ position:"absolute", bottom:80, left:20, zIndex:80, background:"rgba(20,14,0,.92)", border:"1px solid rgba(212,175,55,.3)", borderRadius:10, padding:"12px 16px", maxWidth:340, boxShadow:"0 4px 20px rgba(0,0,0,.5)", animation:"tp-cue-slide-in .2s cubic-bezier(.34,1.46,.64,1) both" }}>
      <div style={{ fontSize:8, letterSpacing:".2em", textTransform:"uppercase", color:"rgba(212,175,55,.6)", marginBottom:6, fontFamily:"'IBM Plex Mono',monospace" }}>
        Presenter Note
      </div>
      {activeNotes.map((n, i) => (
        <div key={i} style={{ fontSize:13, color:"var(--g)", fontFamily:"'IBM Plex Mono',monospace", lineHeight:1.6 }}>{n}</div>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  ToastNotification
// ─────────────────────────────────────────────────────────────
function ToastNotification() {
  const { toastMsg, toastVisible } = useTeleprompter();
  return (
    <div className={`tp-toast${toastVisible ? " show" : ""}`} aria-live="polite">
      {toastMsg}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  ModalsContainer
// ─────────────────────────────────────────────────────────────
function ModalsContainer() {
  const {
    isPro,
    showQR, setShowQR, handleRTCConnected, roomId, initialOffer,
    diffOpen, setDiffOpen, tokens, pendingScript, handleDiffApply,
    showClips, setShowClips, clips, downloadClip, downloadAllClips, deleteClip,
    editOpen, setEditOpen, draftScript, setDraftScript, setDraftCursor, textareaRef, applyScript,
    helpOpen, setHelpOpen,
  } = useTeleprompter();

  return (
    <>
      {isPro && (
        <WebRTCModal
          open={showQR}
          onClose={() => setShowQR(false)}
          onRemoteConnected={handleRTCConnected}
          roomId={roomId}
          initialOffer={initialOffer}
        />
      )}

      <ScriptDiffModal
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        oldTokens={tokens}
        newRaw={pendingScript}
        onApplySections={handleDiffApply}
      />

      {isPro && (
        <ClipsModal
          open={showClips}
          onClose={() => setShowClips(false)}
          clips={clips}
          onDownload={downloadClip}
          onDownloadAll={downloadAllClips}
          onDelete={deleteClip}
        />
      )}

      <EditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        draftScript={draftScript}
        setDraftScript={setDraftScript}
        setDraftCursor={setDraftCursor}
        textareaRef={textareaRef}
        onLoad={() => applyScript()}
      />

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}


// ─────────────────────────────────────────────────────────────
//  CustomCountdownOverlay
// ─────────────────────────────────────────────────────────────
function CustomCountdownOverlay() {
  const {
    isPro, customCdActive, customCdRemaining, stopCustomCountdown, formatCustomCd,
  } = useTeleprompter();

  if (!isPro || !customCdActive) return null;

  return (
    <div className="tp-custom-cd-overlay" onClick={stopCustomCountdown}>
      <div className="tp-custom-cd-label">Countdown</div>
      <div className={`tp-custom-cd-number${customCdRemaining <= 10 ? " urgent" : ""}`}>
        {formatCustomCd(customCdRemaining)}
      </div>
      {customCdRemaining < 60 && <div className="tp-custom-cd-unit">seconds</div>}
      <div className="tp-custom-cd-hint">tap anywhere or press Esc to dismiss</div>
      <button
        className="tp-custom-cd-stop"
        onClick={e => { e.stopPropagation(); stopCustomCountdown(); }}
      >
        Stop Timer
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  TeleprompterApp  — pure composition
// ─────────────────────────────────────────────────────────────
function TeleprompterApp() {
  const { rootRef, mirrored, bgMode } = useTeleprompter();

  return (
    <>
      <GlobalStyles />
      <div
        className={["tp-root", mirrored ? "tp-mirrored" : "", bgMode !== "off" ? "tp-bg-active" : ""].filter(Boolean).join(" ")}
        ref={rootRef}
      >
        <TopBar />
        <PrompterStage />
        <FooterControls />
        <ToastNotification />
        <CuePanelDropdown />
        <SectionPanel />
        <PresenterNotes />
        <ModalsContainer />
        <CustomCountdownOverlay />
      </div>
    </>
  );
}


// ─────────────────────────────────────────────────────────────
//  TeleprompterMain  — default export
// ─────────────────────────────────────────────────────────────
export default function TeleprompterMain({ onLogout }) {
  return (
    <TeleprompterProvider onLogout={onLogout}>
      <TeleprompterApp />
    </TeleprompterProvider>
  );
}